import { Navigate, useNavigate } from "@solidjs/router";
import { onMount, createSignal, createEffect } from "solid-js";
import { useRIASApp } from "~/context/UserProvider";
import { generateCodeVerifier, generateCodeChallenge } from "~/lib";

import { COGNITO_CLIENTID, COGNITO_USERPOOL } from "~/_server/cognito";

import { useProfile } from "~/context/ProfileProvider";
//const cognitoClientId = '2cskdf8d8gff49f8rn1baa6aku';
const oauthHost = `https://${COGNITO_USERPOOL.replace("_","")}.auth.eu-west-2.amazoncognito.com`;
const loginHost = 'https://login.rias-regs.co.uk';

export default () => {
	"use client";
	
	const app = useRIASApp();
	const {updateProfile} = useProfile();

	const [callbackUrl, setCallbackUrl] = createSignal<string>( '' );
	const [loggedIn, setLoggedIn] = createSignal( false );
	const [ready, setReady] = createSignal( false );

	const navigate = useNavigate();

	onMount( () => {
		setReady( true );
		setCallbackUrl( () => `${window.location.origin}/login` )
	} );

	createEffect( () => loggedIn() && navigate( "/dashboard", { replace: true } ) );

	const loginRedirect = async () => {
		const genCodeVerifier = generateCodeVerifier();
		const codeChallenge = await generateCodeChallenge( genCodeVerifier );

		sessionStorage.setItem( "code_verifier", genCodeVerifier );

		const loginUrl = `${loginHost}/login` +
			`?response_type=code` +
			`&client_id=${COGNITO_CLIENTID}` +
			`&redirect_uri=${encodeURIComponent( callbackUrl() )}` +
			`&scope=email+openid+phone` +
			`&code_challenge_method=S256` +
			`&code_challenge=${codeChallenge}`;

		window.location.href = loginUrl;
	}

	createEffect( async () => {
		if ( ready() ) {
			const params = new URLSearchParams( window.location.search );
			const code = params.get( "code" );
			const codeVerifier = sessionStorage.getItem( "code_verifier" );

			if ( !codeVerifier || !code ) {
				await loginRedirect();
			} else {

				const body = new URLSearchParams( {
					grant_type: "authorization_code",
					client_id: COGNITO_CLIENTID,
					code: code ?? "",
					redirect_uri: callbackUrl().toString(),
					code_verifier: codeVerifier!.toString()
				} );

				const res = await fetch( `${oauthHost}/oauth2/token`, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body,
				} );

				const data = await res.json();
				const { access_token: accessToken, id_token: idToken, refresh_token: refreshToken } = { ...data };

				if ( accessToken ) {
					localStorage.setItem( "accessToken", accessToken );
					localStorage.setItem( "idToken", idToken );

					await fetch( '/api/public/auth-cookie', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify( { accessToken, idToken, refreshToken } )
					} );

					setLoggedIn( true );

					updateProfile( 'lastLogin', Date.now() );
				} else {
					await loginRedirect();
				}
			}
		}
	} );

	app.refetchUser();

	return loggedIn() ? <Navigate href="/dashboard" /> : <p class="py-[45vh] text-center text-2xl">Logging you in...</p>;
}
